import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowRight,
  Zap,
  Shield,
  Globe,
  Network,
  User,
  Mail,
  Fingerprint,
  BookOpen,
  Terminal,
  Eye,
  Sparkles,
  Radar,
  ChevronRight,
  Lock,
  GraduationCap,
  Database,
  ChevronDown,
  MousePointer,
  Wrench,
  Skull
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/AppLayout';
import { AnimatedHeroText } from '@/components/AnimatedHeroText';
import { InteractiveConsole } from '@/components/InteractiveConsole';
import { ScrollRevealSection, AnimatedDivider, AnimatedSectionHeader } from '@/components/ScrollRevealSection';
import { PromotionBanner } from '@/components/PromotionBanner';
import { MatrixEffect } from '@/components/MatrixEffect';
import { useFreeMode } from '@/hooks/useFreeMode';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { SEOHead, OrganizationSchema, WebApplicationSchema, WebSiteSchema } from '@/components/seo';

// Public tools accessible without login
const PUBLIC_TOOLS = ['domain_recon', 'ip_intelligence'];

const osintTools = [
  { id: 'domain_recon', icon: Globe, name: 'Domain Recon', desc: 'WHOIS • DNS • SSL', free: true, cost: 0 },
  { id: 'ip_intelligence', icon: Network, name: 'IP Intelligence', desc: 'Geo • ASN • Reputación', free: true, cost: 0 },
  { id: 'email_recon', icon: Fingerprint, name: 'Email Recon', desc: 'Deep OSINT', free: false, cost: 5 },
  { id: 'email_hunter', icon: Mail, name: 'Email Hunter', desc: 'Emails corporativos', free: false, cost: 3 },
  { id: 'username_search', icon: User, name: 'Username Search', desc: '150+ plataformas', free: false, cost: 4 }
];

// Value propositions - redesigned content
const valueProps = [
  { 
    icon: Eye, 
    title: 'Invisibilidad Total', 
    desc: 'Tus objetivos nunca sabrán que fueron investigados. Sin notificaciones, sin rastros, sin registros de IP. Discreción absoluta en cada operación.',
    highlight: true,
    badge: 'ANÓNIMO'
  },
  { 
    icon: Database, 
    title: 'Velocidad Extrema', 
    desc: 'Resultados en milisegundos. Nuestros servidores procesan consultas masivas sin demoras. Investigación profesional a velocidad de rayo.',
    badge: 'RÁPIDO'
  },
  { 
    icon: GraduationCap, 
    title: 'De Novato a Experto', 
    desc: 'Academia completa con guías paso a paso. Desde conceptos básicos hasta técnicas avanzadas de ciberseguridad y OSINT profesional.',
    badge: 'FORMACIÓN'
  },
  { 
    icon: Zap, 
    title: 'Sin Límites, Sin Esperas', 
    desc: 'Herramientas ilimitadas sin necesidad de registro. Accede al instante a reconocimiento de dominios, IPs, emails y más.',
    badge: 'GRATIS'
  },
  {
    icon: Shield,
    title: 'Navaja Suiza del Hacker',
    desc: 'Todo lo que necesitas en un solo lugar: OSINT, generadores, codificadores, dorking, análisis de imágenes y terminal avanzada.',
    badge: 'TODO EN UNO'
  },
  {
    icon: Lock,
    title: 'Privacidad por Diseño',
    desc: 'No pedimos datos personales. Sin cookies de rastreo. Sin analytics invasivos. Tu privacidad es nuestra arquitectura base.',
    badge: 'PRIVADO'
  },
];

// Konami code
const KONAMI_CODE = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];

const Index = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [matrixMode, setMatrixMode] = useState(false);
  const [easterEggFound, setEasterEggFound] = useState(false);
  const [konamiIndex, setKonamiIndex] = useState(0);
  const [showConsole, setShowConsole] = useState(false);
  const { freeMode } = useFreeMode();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Handle tool click - redirect to auth for non-public tools if not logged in
  const handleToolClick = (e: React.MouseEvent, toolId: string) => {
    if (!user && !PUBLIC_TOOLS.includes(toolId)) {
      e.preventDefault();
      navigate('/auth');
    }
  };

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Konami code
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === KONAMI_CODE[konamiIndex]) {
        const newIndex = konamiIndex + 1;
        setKonamiIndex(newIndex);
        if (newIndex === KONAMI_CODE.length) {
          setEasterEggFound(true);
          setKonamiIndex(0);
          setTimeout(() => setEasterEggFound(false), 5000);
        }
      } else {
        setKonamiIndex(0);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [konamiIndex]);

  const handleConsoleEasterEgg = (type: string) => {
    if (type === 'matrix') {
      // Let MatrixEffect manage the full timeline and call onComplete.
      // No external timeout here (it caused premature closing).
      setMatrixMode(true);
    } else if (type === 'easter_egg') {
      setEasterEggFound(true);
      setTimeout(() => setEasterEggFound(false), 4000);
    }
  };

  return (
    <AppLayout showBackground={true}>
      {/* SEO Meta Tags & Structured Data */}
      <SEOHead 
        title="Plataforma OSINT de Inteligencia y Ciberseguridad"
        description="Herramientas profesionales de inteligencia OSINT, academia de ciberseguridad y formación en conciencia digital. Análisis de emails, dominios, IPs y usernames con privacidad total."
        canonical="/"
        keywords={['OSINT', 'ciberseguridad', 'inteligencia digital', 'herramientas OSINT', 'investigación digital']}
      />
      <OrganizationSchema />
      <WebApplicationSchema />
      <WebSiteSchema />
      
      {/* Promotions Banner - Landing */}
      <div className="container mx-auto px-6 sm:px-8 lg:px-12 max-w-6xl pt-4">
        <PromotionBanner placement="landing" />
      </div>
      {/* Matrix Effect - rendered via portal to cover entire screen including header */}
      {createPortal(
        <MatrixEffect 
          active={matrixMode} 
          onComplete={() => setMatrixMode(false)}
        />,
        document.body
      )}

      {/* Easter Egg Notification */}
      {easterEggFound && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-scale-up">
          <div className="px-5 py-3 rounded-xl bg-primary/20 border border-primary/50 backdrop-blur-xl shadow-2xl">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary animate-pulse" />
              <span className="font-mono text-sm text-primary">Easter Egg Desbloqueado!</span>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          HERO SECTION - Mobile First with Layered Approach
      ══════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[100svh] flex flex-col">
        {/* Background effects */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-gradient-radial from-primary/8 to-transparent blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-gradient-radial from-primary/5 to-transparent blur-3xl" />
        </div>

      {/* Main Content - Mobile First - Reduced top spacing */}
        <div className="flex-1 container mx-auto px-6 sm:px-8 lg:px-12 max-w-6xl relative z-10 flex flex-col justify-center py-4 lg:py-8 -mt-8 lg:-mt-12">
          
          {/* Desktop: Grid Layout */}
          <div className="hidden lg:grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Text Content */}
            <div className="flex flex-col items-start text-left">
              {/* Status badge */}
              <div 
                className={cn(
                  "inline-flex items-center gap-2.5 px-4 py-2 rounded-full mb-6",
                  "bg-terminal/10 border border-terminal/30",
                  "transition-all duration-700",
                  isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
                )}
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-terminal opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-terminal" />
                </span>
                <span className="text-sm font-medium text-terminal">Desde Cero a Experto</span>
              </div>

              {/* Title - Animated */}
              <AnimatedHeroText isLoaded={isLoaded} />

              {/* Subtitle */}
              <p 
                className={cn(
                  "text-lg text-muted-foreground max-w-lg mb-8",
                  "transition-all duration-700 delay-200",
                  isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                )}
              >
                Formación en ciberseguridad para todos los niveles, investigación OSINT y suite de utilidades técnicas. Aprende a protegerte y a proteger.
              </p>

              {/* CTAs */}
              <div 
                className={cn(
                  "flex items-center gap-4 mb-8",
                  "transition-all duration-700 delay-300",
                  isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                )}
              >
                <Link to="/tools">
                  <Button 
                    size="lg" 
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-12 text-base font-medium rounded-xl glow-primary"
                  >
                    <Terminal className="w-5 h-5 mr-2" />
                    Iniciar Escaneo
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/knowledge">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="h-12 px-8 text-base rounded-xl border-border hover:bg-muted hover:border-primary/50"
                  >
                    <BookOpen className="w-5 h-5 mr-2" />
                    Conocimiento
                  </Button>
                </Link>
              </div>

              {/* Quick features - Enhanced Distinct Design */}
              <div 
                className={cn(
                  "flex flex-wrap gap-2",
                  "transition-all duration-700 delay-400",
                  isLoaded ? 'opacity-100' : 'opacity-0'
                )}
              >
                {[
                  { icon: Eye, label: 'Sin rastro', gradient: 'from-primary/20 to-primary/5', iconColor: 'text-primary' },
                  { icon: Zap, label: 'Instantáneo', gradient: 'from-warning/20 to-warning/5', iconColor: 'text-warning' },
                  { icon: Lock, label: 'Privado', gradient: 'from-terminal/20 to-terminal/5', iconColor: 'text-terminal' },
                ].map((f, i) => (
                  <div 
                    key={i} 
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 rounded-xl",
                      "bg-gradient-to-r", f.gradient,
                      "border border-border/30 backdrop-blur-sm",
                      "hover:scale-105 transition-transform duration-300"
                    )}
                  >
                    <div className={cn("w-6 h-6 rounded-lg bg-background/50 flex items-center justify-center")}>
                      <f.icon className={cn("w-3.5 h-3.5", f.iconColor)} />
                    </div>
                    <span className="text-sm font-medium text-foreground">{f.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Console */}
            <div 
              className={cn(
                "transition-all duration-700 delay-300",
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              )}
            >
              <InteractiveConsole onEasterEgg={handleConsoleEasterEgg} />
              <p className="text-center text-sm text-muted-foreground mt-3">
                Escribe <code className="px-2 py-0.5 rounded bg-muted text-red-400 font-mono text-xs">help</code> para ver comandos
              </p>
            </div>
          </div>

          {/* Mobile: Clean Structured Layout */}
          <div className="lg:hidden flex flex-col min-h-[calc(100svh-80px)]">
            {/* Hero Content - Centered block with left-aligned text */}
            <div className="flex-1 flex flex-col justify-center px-6 py-8">
              <motion.div 
                className="max-w-sm mx-auto w-full"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                {/* Status badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-terminal/10 border border-terminal/30 mb-5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-terminal opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-terminal" />
                  </span>
                  <span className="text-xs font-medium text-terminal">Desde cero a experto</span>
                </div>

                {/* Title - Left aligned */}
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight mb-2">
                  <span className="block">
                    <span className="text-primary text-glow">Entiende</span>
                    <span className="text-foreground"> los sistemas.</span>
                  </span>
                  <span className="block mt-1">
                    <span className="text-foreground">Practica con </span>
                    <span className="text-primary text-glow">herramientas</span>
                    <span className="text-foreground"> reales.</span>
                  </span>
                </h1>

                {/* Code comment */}
                <p className="font-mono text-[10px] text-muted-foreground/40 mb-4">
                  {"// ready_to_learn"}
                </p>

                {/* Subtitle - Full text */}
                <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                  Formación en ciberseguridad, investigación OSINT y herramientas profesionales.
                </p>

                {/* CTAs - Horizontal compact */}
                <div className="flex items-center gap-3">
                  <Link to="/tools">
                    <Button className="bg-primary hover:bg-primary/90 text-primary-foreground h-10 px-5 text-sm font-medium rounded-xl glow-primary">
                      <Terminal className="w-4 h-4 mr-2" />
                      Iniciar
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                  </Link>
                  <Link to="/knowledge">
                    <Button variant="outline" className="h-10 px-5 text-sm rounded-xl border-border hover:bg-muted">
                      <BookOpen className="w-4 h-4 mr-2" />
                      Conocimiento
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Console Section - With breathing room */}
            <motion.div 
              className="px-6 pb-8 pt-4"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              {!showConsole ? (
                <button
                  onClick={() => setShowConsole(true)}
                  className="w-full p-4 rounded-2xl border border-primary/20 bg-card/80 backdrop-blur-sm hover:border-primary/40 transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
                      <Terminal className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-semibold text-sm mb-0.5">Terminal Interactiva</p>
                      <p className="text-xs text-muted-foreground">Ejecuta comandos OSINT en tiempo real</p>
                    </div>
                    <ChevronDown className="w-5 h-5 text-muted-foreground group-hover:translate-y-0.5 transition-transform" />
                  </div>
                  
                  {/* Fake terminal preview */}
                  <div className="mt-3 p-3 rounded-lg bg-background/50 border border-border/30 font-mono text-xs text-left">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <span className="text-primary">$</span>
                      <span className="text-foreground/70">help</span>
                      <span className="animate-pulse">_</span>
                    </div>
                  </div>
                </button>
              ) : (
                <div className="space-y-3">
                  <InteractiveConsole onEasterEgg={handleConsoleEasterEgg} compact />
                  <button
                    onClick={() => setShowConsole(false)}
                    className="w-full py-2 text-xs text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/50"
                  >
                    Ocultar terminal
                  </button>
                </div>
              )}
            </motion.div>

            {/* Scroll hint */}
            <motion.div 
              className="flex flex-col items-center pb-4 text-muted-foreground"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <ChevronDown className="w-5 h-5 animate-bounce" />
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator - Desktop only */}
        <div className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-muted-foreground animate-bounce">
          <MousePointer className="w-4 h-4" />
          <span className="text-xs font-mono">Scroll</span>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          ANIMATED SECTION DIVIDER - Tools
      ══════════════════════════════════════════════════════════════ */}
      <AnimatedDivider 
        icon={<Database className="w-6 h-6" />}
        label="Arsenal OSINT"
        sublabel="Inteligencia digital avanzada"
      />

      {/* ══════════════════════════════════════════════════════════════
          OSINT TOOLS SECTION
      ══════════════════════════════════════════════════════════════ */}
      <section className="relative py-12 sm:py-16 bg-gradient-to-b from-muted/5 to-background">
        {/* Section background pattern */}
        <div className="absolute inset-0 opacity-[0.02] bg-grid pointer-events-none" />
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 max-w-6xl relative z-10">
          {/* Section Header */}
          <AnimatedSectionHeader
            badge="Herramientas"
            badgeIcon={<Database className="w-4 h-4 text-primary" />}
            title={<>Módulos de <span className="text-primary">Reconocimiento</span></>}
            subtitle="Suite completa para análisis e investigación de inteligencia digital"
          />

          {/* Tools Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {osintTools.map((tool, i) => {
              const isPublicTool = PUBLIC_TOOLS.includes(tool.id);
              const requiresLogin = !user && !isPublicTool;
              
              return (
                <ScrollRevealSection key={tool.id} delay={i * 80}>
                  <Link
                    to={`/tools?tool=${tool.id}`}
                    onClick={(e) => handleToolClick(e, tool.id)}
                    className={cn(
                      "group p-5 rounded-2xl transition-all duration-300 relative overflow-hidden block h-full",
                      "bg-card border border-border hover:border-primary/50",
                      "hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1",
                      requiresLogin && "opacity-85"
                    )}
                  >
                    {/* Lock overlay for non-public tools when not logged in */}
                    {requiresLogin && (
                      <div className="absolute top-2 right-2 z-20">
                        <div className="w-6 h-6 rounded-full bg-muted/80 flex items-center justify-center">
                          <Lock className="w-3 h-3 text-muted-foreground" />
                        </div>
                      </div>
                    )}
                    
                    {/* Gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    
                    <div className="relative z-10">
                      <div className="flex items-start gap-4 sm:block">
                        <div className={cn(
                          "w-12 h-12 rounded-xl flex items-center justify-center sm:mb-4",
                          "bg-primary/10 group-hover:bg-primary",
                          "transition-all duration-300"
                        )}>
                          <tool.icon className="w-6 h-6 text-primary group-hover:text-primary-foreground transition-colors" />
                        </div>
                        
                        <div className="flex-1 sm:flex-none">
                          <div className="flex items-center gap-2 mb-1.5">
                            <h3 className="font-semibold group-hover:text-primary transition-colors text-sm sm:text-base">
                              {tool.name}
                            </h3>
                            {/* Show cost badge when NOT in free mode */}
                            {!freeMode && (
                              <span className={cn(
                                "px-1.5 py-0.5 rounded text-[10px] font-medium flex items-center gap-0.5",
                                tool.free 
                                  ? "bg-terminal/20 text-terminal" 
                                  : "bg-primary/20 text-primary"
                              )}>
                                <Zap className="w-2.5 h-2.5" />
                                {tool.free ? 'Free' : '3'}
                              </span>
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-muted-foreground">{tool.desc}</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                </ScrollRevealSection>
              );
            })}
          </div>

          {/* View all CTA */}
          <ScrollRevealSection delay={500}>
            <div className="text-center mt-10">
              <Link to="/tools">
                <Button variant="outline" className="rounded-xl gap-2 px-6">
                  Ver todas las herramientas
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </ScrollRevealSection>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          ANIMATED SECTION DIVIDER - Why Us
      ══════════════════════════════════════════════════════════════ */}
      <AnimatedDivider 
        icon={<Skull className="w-6 h-6" />}
        label="Tu Arsenal Completo"
        sublabel="Todo lo que necesitas"
      />

      {/* ══════════════════════════════════════════════════════════════
          WHY LOWKEYX SECTION - Completely Redesigned
      ══════════════════════════════════════════════════════════════ */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-gradient-radial from-primary/8 to-transparent blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-gradient-radial from-terminal/5 to-transparent blur-3xl" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl relative z-10">
          {/* Section Header */}
          <ScrollRevealSection>
            <div className="text-center mb-12 sm:mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
                <Wrench className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">La Navaja Suiza del Hacker</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
                Una plataforma. <span className="text-primary text-glow">Infinitas posibilidades.</span>
              </h2>
              <p className="text-muted-foreground max-w-3xl mx-auto text-sm sm:text-base leading-relaxed">
                Herramientas OSINT profesionales, formación integral y máxima privacidad. 
                Desde principiantes curiosos hasta expertos en ciberseguridad.
              </p>
            </div>
          </ScrollRevealSection>

          {/* Bento Grid - Flexible layout without gaps */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            
            {/* Card 1 - Invisibilidad (Wide) */}
            <ScrollRevealSection delay={0} className="col-span-2 lg:col-span-2">
              <div className="relative h-full min-h-[200px] rounded-2xl overflow-hidden group cursor-default">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/25 via-primary/10 to-background" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/40 via-transparent to-transparent opacity-60" />
                <div className="absolute inset-0 opacity-[0.07]" style={{ 
                  backgroundImage: 'linear-gradient(to right, hsl(var(--primary)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--primary)) 1px, transparent 1px)',
                  backgroundSize: '24px 24px'
                }} />
                <div className="absolute -top-12 -right-12 w-36 h-36 bg-primary/30 rounded-full blur-[60px] group-hover:scale-125 transition-transform duration-700" />
                <div className="absolute inset-0 rounded-2xl border border-primary/40 group-hover:border-primary/60 transition-colors" />
                
                <div className="relative z-10 h-full p-4 sm:p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/20 backdrop-blur-sm flex items-center justify-center border border-primary/50 shadow-lg shadow-primary/25 group-hover:scale-110 transition-transform flex-shrink-0">
                        <Eye className="w-5 h-5 text-primary" />
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-primary text-primary-foreground">
                        {valueProps[0].badge}
                      </span>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold mb-1.5 leading-tight group-hover:text-primary transition-colors">
                      {valueProps[0].title}
                    </h3>
                    <p className="text-muted-foreground text-xs leading-relaxed line-clamp-2 sm:line-clamp-3">
                      {valueProps[0].desc}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-primary/20">
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-terminal animate-pulse" />
                      <span className="text-[10px] font-mono text-terminal">STEALTH</span>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollRevealSection>

            {/* Card 2 - Velocidad */}
            <ScrollRevealSection delay={100} className="col-span-1">
              <div className="relative h-full min-h-[280px] sm:min-h-[200px] rounded-2xl overflow-hidden group bg-card border border-border hover:border-info/50 transition-all cursor-default">
                <div className="absolute inset-0 bg-gradient-to-br from-info/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                
                <div className="relative z-10 h-full p-4 flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-info/15 flex items-center justify-center border border-info/30 group-hover:scale-110 transition-transform flex-shrink-0">
                      <Zap className="w-4 h-4 text-info" />
                    </div>
                    <span className="px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider bg-info/20 text-info border border-info/30">
                      {valueProps[1].badge}
                    </span>
                  </div>
                  <h3 className="font-bold text-sm sm:text-base mb-1 group-hover:text-info transition-colors">
                    {valueProps[1].title}
                  </h3>
                  <p className="text-[10px] sm:text-xs text-muted-foreground leading-relaxed flex-1">
                    {valueProps[1].desc}
                  </p>
                  <div className="mt-2 pt-2 border-t border-border/50">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-info animate-pulse" />
                      <span className="text-[9px] font-mono text-info/80">&lt;50ms</span>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollRevealSection>

            {/* Card 3 - Privacidad */}
            <ScrollRevealSection delay={150} className="col-span-1">
              <div className="relative h-full min-h-[280px] sm:min-h-[200px] rounded-2xl overflow-hidden group bg-card border border-border hover:border-rose-500/50 transition-all cursor-default">
                <div className="absolute inset-0 bg-gradient-to-br from-rose-500/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                
                <div className="relative z-10 h-full p-4 flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-rose-500/15 flex items-center justify-center border border-rose-500/30 group-hover:scale-110 transition-transform flex-shrink-0">
                      <Lock className="w-4 h-4 text-rose-400" />
                    </div>
                    <span className="px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider bg-rose-500/20 text-rose-400 border border-rose-500/30">
                      {valueProps[5].badge}
                    </span>
                  </div>
                  <h3 className="font-bold text-sm sm:text-base mb-1 group-hover:text-rose-400 transition-colors">
                    {valueProps[5].title}
                  </h3>
                  <p className="text-[10px] sm:text-xs text-muted-foreground leading-relaxed flex-1">
                    {valueProps[5].desc}
                  </p>
                  <div className="mt-2 pt-2 border-t border-border/50">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
                      <span className="text-[9px] font-mono text-rose-400/80">ZERO LOGS</span>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollRevealSection>

            {/* Card 4 - Formación */}
            <ScrollRevealSection delay={200} className="col-span-1">
              <div className="relative h-full min-h-[290px] sm:min-h-[210px] rounded-2xl overflow-hidden group bg-card border border-border hover:border-terminal/50 transition-all cursor-default">
                <div className="absolute inset-0 bg-gradient-to-br from-terminal/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                
                <div className="relative z-10 h-full p-4 flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-terminal/15 flex items-center justify-center border border-terminal/30 group-hover:scale-110 transition-transform flex-shrink-0">
                      <GraduationCap className="w-4 h-4 text-terminal" />
                    </div>
                    <span className="px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider bg-terminal/20 text-terminal border border-terminal/30">
                      {valueProps[2].badge}
                    </span>
                  </div>
                  <h3 className="font-bold text-sm sm:text-base mb-1 group-hover:text-terminal transition-colors">
                    {valueProps[2].title}
                  </h3>
                  <p className="text-[10px] sm:text-xs text-muted-foreground leading-relaxed flex-1">
                    {valueProps[2].desc}
                  </p>
                  <div className="mt-2 pt-2 border-t border-border/50">
                    <Link to="/knowledge" className="flex items-center gap-1.5 text-terminal hover:gap-2 transition-all">
                      <span className="text-[10px] font-semibold">Academia</span>
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            </ScrollRevealSection>

            {/* Card 5 - Gratis */}
            <ScrollRevealSection delay={250} className="col-span-1">
              <div className="relative h-full min-h-[290px] sm:min-h-[210px] rounded-2xl overflow-hidden group bg-card border border-border hover:border-warning/50 transition-all cursor-default">
                <div className="absolute inset-0 bg-gradient-to-br from-warning/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                
                <div className="relative z-10 h-full p-4 flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-warning/15 flex items-center justify-center border border-warning/30 group-hover:scale-110 transition-transform flex-shrink-0">
                      <Zap className="w-4 h-4 text-warning" />
                    </div>
                    <span className="px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider bg-warning/20 text-warning border border-warning/30">
                      {valueProps[3].badge}
                    </span>
                  </div>
                  <h3 className="font-bold text-sm sm:text-base mb-1 group-hover:text-warning transition-colors">
                    {valueProps[3].title}
                  </h3>
                  <p className="text-[10px] sm:text-xs text-muted-foreground leading-relaxed flex-1">
                    {valueProps[3].desc}
                  </p>
                  <div className="mt-2 pt-2 border-t border-border/50">
                    <Link to="/tools" className="flex items-center gap-1.5 text-warning hover:gap-2 transition-all">
                      <span className="text-[10px] font-semibold">Probar</span>
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            </ScrollRevealSection>

            {/* Card 6 - Todo en Uno (Wide) */}
            <ScrollRevealSection delay={300} className="col-span-2 lg:col-span-2">
              <div className="relative h-full min-h-[200px] rounded-2xl overflow-hidden group bg-gradient-to-r from-purple-500/10 via-card to-fuchsia-500/10 border border-purple-500/30 hover:border-purple-500/50 transition-all cursor-default">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-purple-500/20 via-transparent to-transparent opacity-50" />
                <div className="absolute right-0 top-0 bottom-0 w-1/4 bg-gradient-to-l from-fuchsia-500/10 to-transparent" />
                {/* Grid pattern background */}
                <div className="absolute inset-0 opacity-[0.07]" style={{ 
                  backgroundImage: 'linear-gradient(to right, rgb(168, 85, 247) 1px, transparent 1px), linear-gradient(to bottom, rgb(168, 85, 247) 1px, transparent 1px)',
                  backgroundSize: '24px 24px'
                }} />
                
                <div className="relative z-10 h-full p-4 sm:p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 backdrop-blur-sm flex items-center justify-center border border-purple-500/50 shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform flex-shrink-0">
                        <Shield className="w-5 h-5 text-purple-400" />
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-purple-500 text-white">
                        {valueProps[4].badge}
                      </span>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold mb-1.5 leading-tight group-hover:text-purple-400 transition-colors">
                      {valueProps[4].title}
                    </h3>
                    <p className="text-muted-foreground text-xs leading-relaxed line-clamp-2 sm:line-clamp-3">
                      {valueProps[4].desc}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-purple-500/20">
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
                      <span className="text-[10px] font-mono text-purple-400">15+ TOOLS</span>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollRevealSection>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          ANIMATED SECTION DIVIDER - CTA
      ══════════════════════════════════════════════════════════════ */}
      <AnimatedDivider 
        icon={<Radar className="w-6 h-6" />}
        label="Comenzar"
      />

      {/* ══════════════════════════════════════════════════════════════
          FINAL CTA SECTION
      ══════════════════════════════════════════════════════════════ */}
      <section className="relative py-12 sm:py-16">
        <div className="absolute inset-0 bg-gradient-to-t from-primary/5 via-transparent to-transparent" />
        
        <ScrollRevealSection>
          <div className="container mx-auto px-6 sm:px-8 lg:px-12 max-w-2xl relative z-10 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto mb-6">
              <Radar className="w-8 h-8 text-primary animate-pulse" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Comienza tu investigación
            </h2>
            <p className="text-muted-foreground mb-8 text-sm sm:text-base">
              Accede a herramientas profesionales de inteligencia digital y lleva tus investigaciones al siguiente nivel.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/tools">
                <Button size="lg" className="w-full sm:w-auto bg-primary hover:bg-primary/90 glow-primary rounded-xl px-8 h-12 gap-2">
                  <Terminal className="w-5 h-5" />
                  Acceder a Herramientas
                </Button>
              </Link>
              <Link to="/auth">
                <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-xl px-8 h-12">
                  Crear Cuenta Gratuita
                </Button>
              </Link>
            </div>
          </div>
        </ScrollRevealSection>
      </section>

    </AppLayout>
  );
};

export default Index;
